import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { newCoverEffect, type CoverDoc, type CoverEffect, type CoverEffectType } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { CoverThumb } from '../canvas/CoverThumb';
import { effectMessages } from '../coverEditorMessages';
import { Toggle } from '../inspector/controls/Toggle';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface EffectTileProps {
  type: CoverEffectType;
  /** The cover with no effects: the tile shows the effect on it. */
  base: CoverDoc;
  /** The effect of this type on the cover, when it is on. */
  active: CoverEffect | undefined;
  isSelected: boolean;
  disabled: boolean;
  onToggle: (type: CoverEffectType, isOn: boolean) => void;
  onSelect: (id: string) => void;
}

const TILE_THUMB_PX = 120;

/** One effect of the Effects panel (design cover-editor d3): the effect on this cover, its name and its switch. */
const EffectTileComponent: React.FC<EffectTileProps> = (props) => {
  const { type, base, active, isSelected, disabled, onToggle, onSelect } = props;
  const intl = useIntl();
  const name = intl.formatMessage(effectMessages[type].name);
  const preview = useMemo<CoverDoc>(
    () => ({ ...base, effects: [active ?? newCoverEffect(type, `preview_${type}`, base.palette)] }),
    [active, base, type]
  );
  return (
    <div
      className={clsx(
        styles.effectTile,
        active !== undefined && styles.effectTileOn,
        isSelected && styles.effectTileSelected
      )}
    >
      <button
        type="button"
        className={styles.effectThumb}
        aria-label={name}
        disabled={disabled || active === undefined}
        onClick={() => {
          if (active !== undefined) {
            onSelect(active.id);
          }
        }}
      >
        <CoverThumb doc={preview} size={TILE_THUMB_PX} />
      </button>
      <span className={styles.effectName}>
        {name}
        <Toggle
          label={name}
          isOn={active !== undefined}
          disabled={disabled}
          onChange={(isOn) => onToggle(type, isOn)}
        />
      </span>
    </div>
  );
};

export const EffectTile = TypedMemo(EffectTileComponent);
